import React, { useMemo, useState } from 'react';
import { Alert, Button, Form, Modal, Spinner, Table } from 'react-bootstrap';

import api from '../services/api';
import {
  ProjectCostCompany,
  ProjectCostCollaboratorImportJiraUsersRequest,
  ProjectCostCollaboratorImportJiraUserItem,
  ProjectCostCollaboratorImportJiraUserSelectionItem,
  ProjectCostCollaboratorImportJiraUsersResponse,
  ProjectCostRole,
} from '../types';

type Props = {
  show: boolean;
  onHide: () => void;
  onImported: () => Promise<void>;
  companies?: ProjectCostCompany[];
  roles?: ProjectCostRole[];
  showAlert: (params: { title: string; message: string; variant?: 'success' | 'warning' | 'danger' | 'info' }) => void;
};

const ProjectCostCollaboratorImportJiraUsersModal: React.FC<Props> = ({
  show,
  onHide,
  onImported,
  companies,
  roles,
  showAlert,
}) => {
  const [dryRun, setDryRun] = useState(true);
  const [linkUsers, setLinkUsers] = useState(true);
  const [isThirdParty, setIsThirdParty] = useState(false);
  const [hasThirdPartyOverride, setHasThirdPartyOverride] = useState(false);
  const [limit, setLimit] = useState('2000');
  const [defaultCompanyId, setDefaultCompanyId] = useState('');
  const [defaultRoleId, setDefaultRoleId] = useState('');
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState<ProjectCostCollaboratorImportJiraUsersResponse | null>(null);
  const [jiraRows, setJiraRows] = useState<ProjectCostCollaboratorImportJiraUserItem[]>([]);
  const companyOptions = useMemo(() => (Array.isArray(companies) ? companies : []), [companies]);
  const roleOptions = useMemo(() => (Array.isArray(roles) ? roles : []), [roles]);
  const errorItems = useMemo(() => result?.errors ?? [], [result]);

  const hasErrors = useMemo(() => errorItems.length > 0, [errorItems]);
  const selectedCount = useMemo(() => jiraRows.filter((item) => item.selected && !item.already_exists).length, [jiraRows]);
  const hasLoadedRows = jiraRows.length > 0;

  const resetState = () => {
    setDryRun(true);
    setLinkUsers(true);
    setIsThirdParty(false);
    setHasThirdPartyOverride(false);
    setLimit('2000');
    setDefaultCompanyId('');
    setDefaultRoleId('');
    setLoading(false);
    setResult(null);
    setJiraRows([]);
  };

  const handleClose = () => {
    resetState();
    onHide();
  };

  const buildBasePayload = (): ProjectCostCollaboratorImportJiraUsersRequest => {
    const parsedLimit = Math.trunc(Number(limit));
    return {
      dry_run: dryRun,
      limit: parsedLimit,
      link_users: linkUsers,
      default_company_id: defaultCompanyId ? Number(defaultCompanyId) : null,
      default_role_id: defaultRoleId ? Number(defaultRoleId) : null,
    };
  };

  const inferIsThirdPartyFromCompanyId = (companyId?: number | null): boolean => {
    const companyName = companyOptions.find((item) => item.id === companyId)?.name || '';
    return !companyName.toLowerCase().includes('petronect');
  };

  const validateLimit = (): boolean => {
    const parsedLimit = Number(limit);
    if (!Number.isFinite(parsedLimit) || parsedLimit < 1 || parsedLimit > 20000) {
      showAlert({
        title: 'Limite inválido',
        message: 'Informe um limite entre 1 e 20000.',
        variant: 'warning',
      });
      return false;
    }
    return true;
  };

  const loadJiraUsers = async () => {
    if (!validateLimit()) return;

    setLoading(true);
    try {
      const payload: ProjectCostCollaboratorImportJiraUsersRequest = {
        ...buildBasePayload(),
        ...(hasThirdPartyOverride ? { is_third_party: isThirdParty } : {}),
        dry_run: true,
        users: [],
      };
      const { data } = await api.post<ProjectCostCollaboratorImportJiraUsersResponse>(
        '/api/projects/costs/collaborators/import-jira-users',
        payload,
      );
      setJiraRows(data.items || []);
      setResult(data);
      showAlert({
        title: 'Usuários Jira carregados',
        message: `Foram retornados ${data.total_users} usuários. Selecione quem deseja importar e ajuste empresa/função por linha.`,
        variant: 'info',
      });
    } catch (err: any) {
      showAlert({
        title: 'Erro ao buscar usuários do Jira',
        message: err?.response?.data?.detail || 'Não foi possível carregar usuários ativos do Jira.',
        variant: 'danger',
      });
    } finally {
      setLoading(false);
    }
  };

  const updateRow = (rowNumber: number, patch: Partial<ProjectCostCollaboratorImportJiraUserItem>) => {
    setJiraRows((prev) => prev.map((item) => (item.row_number === rowNumber ? { ...item, ...patch } : item)));
  };

  const toggleSelectAllNew = (selected: boolean) => {
    setJiraRows((prev) => prev.map((item) => (item.already_exists ? item : { ...item, selected })));
  };

  const submitImport = async () => {
    if (!hasLoadedRows) {
      showAlert({
        title: 'Carregue usuários primeiro',
        message: 'Clique em "Buscar usuários do Jira" antes de importar.',
        variant: 'warning',
      });
      return;
    }
    if (!validateLimit()) return;
    if (selectedCount <= 0) {
      showAlert({
        title: 'Nenhum usuário selecionado',
        message: 'Selecione ao menos um usuário para importação.',
        variant: 'warning',
      });
      return;
    }

    const missingCompany = jiraRows.find((item) => item.selected && !item.already_exists && !item.company_id);
    if (missingCompany) {
      showAlert({
        title: 'Empresa obrigatória',
        message: `Informe a empresa para o usuário "${missingCompany.display_name}".`,
        variant: 'warning',
      });
      return;
    }

    const users: ProjectCostCollaboratorImportJiraUserSelectionItem[] = jiraRows.map((item) => ({
      row_number: item.row_number,
      selected: Boolean(item.selected && !item.already_exists),
      account_id: item.account_id ?? null,
      display_name: item.display_name ?? null,
      email: item.email ?? null,
      company_id: item.company_id ?? null,
      role_id: item.role_id ?? null,
      is_third_party: item.is_third_party,
    }));

    setLoading(true);
    try {
      const payload: ProjectCostCollaboratorImportJiraUsersRequest = {
        ...buildBasePayload(),
        ...(hasThirdPartyOverride ? { is_third_party: isThirdParty } : {}),
        users,
      };
      const { data } = await api.post<ProjectCostCollaboratorImportJiraUsersResponse>(
        '/api/projects/costs/collaborators/import-jira-users',
        payload,
      );
      setResult(data);

      if (!dryRun && data.created > 0) {
        await onImported();
      }

      showAlert({
        title: data.errors.length ? 'Importação Jira concluída com pendências' : 'Importação Jira concluída',
        message: data.errors.length
          ? `Criados: ${data.created}. Pulados: ${data.skipped}. Erros: ${data.errors.length}.`
          : `Importação aplicada. Criados: ${data.created}. Pulados: ${data.skipped}.`,
        variant: data.errors.length ? 'warning' : 'success',
      });
    } catch (err: any) {
      showAlert({
        title: 'Erro ao importar usuários do Jira',
        message: err?.response?.data?.detail || 'Não foi possível importar usuários selecionados do Jira.',
        variant: 'danger',
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <Modal show={show} onHide={handleClose} centered size="lg">
      <Modal.Header closeButton>
        <Modal.Title>Importar colaboradores do Jira</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <Alert variant="info" className="mb-3">
          Este fluxo é manual: primeiro carregue os usuários do Jira, depois selecione individualmente quem será cadastrado,
          definindo <strong>empresa</strong> e <strong>função</strong> por linha antes de importar.
        </Alert>

        <Form.Group className="mb-3">
          <Form.Label>Quantidade máxima de usuários a buscar</Form.Label>
          <Form.Control
            type="number"
            min={1}
            max={20000}
            step={1}
            value={limit}
            onChange={(event) => setLimit(event.target.value)}
            disabled={loading}
          />
          <Form.Text className="text-muted">
            Use um valor alto para carga inicial e valores menores para reprocessamentos rápidos.
          </Form.Text>
        </Form.Group>
        <Form.Group className="mb-2">
          <Form.Label>Empresa padrão para pré-preenchimento</Form.Label>
          <Form.Select
            value={defaultCompanyId}
            onChange={(event) => setDefaultCompanyId(event.target.value)}
            disabled={loading}
          >
            <option value="">Sem padrão</option>
            {companyOptions.map((company) => (
              <option key={company.id} value={company.id}>{company.name}</option>
            ))}
          </Form.Select>
        </Form.Group>
        <Form.Group className="mb-3">
          <Form.Label>Função padrão para pré-preenchimento</Form.Label>
          <Form.Select
            value={defaultRoleId}
            onChange={(event) => setDefaultRoleId(event.target.value)}
            disabled={loading}
          >
            <option value="">Sem padrão</option>
            {roleOptions.map((role) => (
              <option key={role.id} value={role.id}>{role.name}</option>
            ))}
          </Form.Select>
        </Form.Group>

        <Form.Check
          id="jira-import-dry-run"
          className="mb-2"
          label="Executar simulação (não grava no banco)"
          checked={dryRun}
          onChange={(event) => setDryRun(event.target.checked)}
          disabled={loading}
        />
        <Form.Check
          id="jira-import-link-users"
          className="mb-2"
          label="Vincular automaticamente com usuário interno pelo e-mail"
          checked={linkUsers}
          onChange={(event) => setLinkUsers(event.target.checked)}
          disabled={loading}
        />
        <Form.Check
          id="jira-import-third-party"
          className="mb-3"
          label="Forçar classificação global de terceiros (sobrepõe sugestão por empresa)"
          checked={isThirdParty}
          onChange={(event) => {
            setIsThirdParty(event.target.checked);
            setHasThirdPartyOverride(true);
          }}
          disabled={loading}
        />

        {hasLoadedRows && (
          <div className="mb-3">
            <div className="d-flex align-items-center justify-content-between mb-2">
              <div className="fw-semibold">Usuários retornados do Jira</div>
              <div className="d-flex gap-2">
                <Button size="sm" variant="outline-secondary" onClick={() => toggleSelectAllNew(true)} disabled={loading}>
                  Marcar todos novos
                </Button>
                <Button size="sm" variant="outline-secondary" onClick={() => toggleSelectAllNew(false)} disabled={loading}>
                  Desmarcar todos
                </Button>
              </div>
            </div>
            <div className="text-muted mb-2">Selecionados para importação: {selectedCount}</div>
            <div style={{ maxHeight: 360, overflowY: 'auto' }}>
              <Table bordered hover size="sm" className="mb-0">
                <thead>
                  <tr>
                    <th>Importar</th>
                    <th>Nome Jira</th>
                    <th>E-mail</th>
                    <th>Empresa</th>
                    <th>Função</th>
                    <th>Terceiro</th>
                    <th>Status</th>
                  </tr>
                </thead>
                <tbody>
                  {jiraRows.map((item) => (
                    <tr key={`${item.row_number}-${item.account_id || item.email || item.display_name}`}>
                      <td>
                        <Form.Check
                          checked={Boolean(item.selected && !item.already_exists)}
                          onChange={(event) => updateRow(item.row_number, { selected: event.target.checked })}
                          disabled={loading || item.already_exists}
                        />
                      </td>
                      <td>
                        <div>{item.display_name || '—'}</div>
                        <div className="text-muted small">{item.account_id || 'Sem accountId'}</div>
                      </td>
                      <td>{item.email || '—'}</td>
                      <td style={{ minWidth: 180 }}>
                        <Form.Select
                          value={item.company_id ?? ''}
                          onChange={(event) => {
                            const value = event.target.value;
                            const companyId = value ? Number(value) : null;
                            updateRow(item.row_number, {
                              company_id: companyId,
                              is_third_party: inferIsThirdPartyFromCompanyId(companyId),
                            });
                          }}
                          disabled={loading || item.already_exists}
                        >
                          <option value="">Selecione</option>
                          {companyOptions.map((company) => (
                            <option key={company.id} value={company.id}>{company.name}</option>
                          ))}
                        </Form.Select>
                      </td>
                      <td style={{ minWidth: 160 }}>
                        <Form.Select
                          value={item.role_id ?? ''}
                          onChange={(event) => {
                            const value = event.target.value;
                            updateRow(item.row_number, { role_id: value ? Number(value) : null });
                          }}
                          disabled={loading || item.already_exists}
                        >
                          <option value="">Sem função</option>
                          {roleOptions.map((role) => (
                            <option key={role.id} value={role.id}>{role.name}</option>
                          ))}
                        </Form.Select>
                      </td>
                      <td>
                        <Form.Check
                          checked={Boolean(item.is_third_party)}
                          onChange={(event) => updateRow(item.row_number, { is_third_party: event.target.checked })}
                          disabled={loading || item.already_exists}
                        />
                      </td>
                      <td className="small">
                        {item.already_exists ? (
                          <span className="text-muted">Já cadastrado</span>
                        ) : item.message ? (
                          <span className="text-warning">{item.message}</span>
                        ) : (
                          <span className="text-success">Novo</span>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </Table>
            </div>
          </div>
        )}

        {result && (
          <div className="border rounded p-3 bg-light">
            <div className="fw-semibold mb-2">Resultado</div>
            <div>Total lido no Jira: {result.total_users}</div>
            <div>Criados: {result.created}</div>
            <div>Pulados (já existentes): {result.skipped}</div>
            <div>Erros: {errorItems.length}</div>
            {result.preview_only && <div className="text-muted">Pré-visualização carregada. Nenhuma alteração foi persistida.</div>}
            {result.dry_run && !result.preview_only && (
              <div className="text-muted">Modo simulação: nenhuma alteração foi persistida.</div>
            )}

            {hasErrors && (
              <div className="mt-3">
                <div className="fw-semibold mb-2">Pendências</div>
                <div style={{ maxHeight: 220, overflowY: 'auto' }}>
                  <Table bordered hover size="sm" className="mb-0">
                    <thead>
                      <tr>
                        <th>#</th>
                        <th>Mensagem</th>
                        <th>Nome</th>
                        <th>E-mail</th>
                        <th>Jira accountId</th>
                      </tr>
                    </thead>
                    <tbody>
                      {errorItems.map((item, idx) => (
                        <tr key={`${item.row_number}-${idx}`}>
                          <td>{item.row_number}</td>
                          <td>{item.message}</td>
                          <td>{item.name || '—'}</td>
                          <td>{item.email || '—'}</td>
                          <td>{item.jira_account_id || '—'}</td>
                        </tr>
                      ))}
                    </tbody>
                  </Table>
                </div>
              </div>
            )}
          </div>
        )}
      </Modal.Body>
      <Modal.Footer>
        <Button variant="outline-secondary" onClick={handleClose} disabled={loading}>
          Fechar
        </Button>
        <Button variant="outline-primary" onClick={() => void loadJiraUsers()} disabled={loading}>
          {loading ? 'Buscando...' : 'Buscar usuários do Jira'}
        </Button>
        <Button variant="primary" onClick={() => void submitImport()} disabled={loading}>
          {loading ? (
            <>
              <Spinner animation="border" size="sm" className="me-2" />
              Processando...
            </>
          ) : dryRun ? (
            'Simular importação selecionada'
          ) : (
            'Importar selecionados'
          )}
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

export default ProjectCostCollaboratorImportJiraUsersModal;
